import {Component} from 'react';
import type {DatePickerProps} from 'react-datepicker';
import ReactDatePicker, {registerLocale} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import Color from 'color';
import {enGB} from 'date-fns/locale/en-GB';
import styled from 'styled-components';

import {getColor} from '@yourcoach/shared/styles/utils';

registerLocale('en-GB', enGB);

const StyledCustomInput = styled.button`
  padding: 0 5px;
  height: 26px;
  font-size: 15px;
  line-height: 22px;
  background: ${p => p.theme.color.background1};
  color: ${p => p.theme.color.text1};
  border: 1px solid ${p => p.theme.color.primary1};
  border-radius: 4px;
  cursor: pointer;

  &::placeholder {
    color: ${p => p.theme.color.text6};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 4px 1px ${p => p.theme.color.secondary1};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

interface DatePickerCustomInputProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  value: Date;
}

class CustomInput extends Component<DatePickerCustomInputProps> {
  override render() {
    const {onClick, value} = this.props;

    return (
      <StyledCustomInput onClick={onClick}>
        {/* @ts-expect-error: value is a date */}
        {value || 'Press to select date'}
      </StyledCustomInput>
    );
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .react-datepicker {
    border: none;
    background-color: ${getColor('background1')};
  }

  &&
    .react-datepicker-popper[data-placement^='top']
    .react-datepicker__triangle::after {
    border-top-color: ${getColor('background1')};
  }

  &&
    .react-datepicker-popper[data-placement^='bottom']
    .react-datepicker__triangle::before {
    border-bottom-color: ${getColor('border2')};
  }

  &&
    .react-datepicker-popper[data-placement^='bottom']
    .react-datepicker__triangle::after {
    border-bottom-color: ${getColor('background1')};
  }

  &&
    .react-datepicker-popper[data-placement^='top']
    .react-datepicker__triangle::before {
    border-top-color: ${getColor('border2')};
  }

  && .react-datepicker__navigation-icon--previous::before {
    border-color: ${getColor('text3')};
    border-width: 2px 2px 0 0;
    top: 12px;
  }

  && .react-datepicker__navigation-icon--next::before {
    border-color: ${getColor('text3')};
    border-width: 2px 2px 0 0;
    top: 12px;
  }

  && .react-datepicker__header {
    background-color: ${getColor('background1')};
    border-bottom-color: ${getColor('border2')};
  }

  && .react-datepicker__day-name {
    color: ${getColor('text1')};
  }

  && .react-datepicker__current-month {
    color: ${getColor('text1')};
  }

  && .react-datepicker__day {
    border-radius: 50%;
    color: ${getColor('text1')};
  }

  && .react-datepicker__day--outside-month {
    color: ${getColor('text3')};
  }

  && .react-datepicker__day--today {
    background-color: transparent;
  }

  && .react-datepicker__day--selected,
  && .react-datepicker__day--in-range,
  && .react-datepicker__day--in-range:hover {
    background-color: ${getColor('global1')} !important;
    color: ${getColor('text.white')};
  }

  && .react-datepicker__day--in-selecting-range {
    background-color: ${p =>
      Color(getColor('global1')(p)).alpha(0.5).toString()} !important;
  }

  && .react-datepicker__day--disabled {
    color: ${p => Color(getColor('text3')(p)).alpha(0.5).toString()};
  }

  && .react-datepicker__day--selected.react-datepicker__day--selected:hover {
    background-color: ${getColor('global1')};
    border-radius: 50%;
  }

  && .react-datepicker__day--selected.react-datepicker__day--selected:focus {
    box-shadow: none;
  }

  && .react-datepicker__day--keyboard-selected {
    background-color: ${getColor('background1')};
  }

  &&
    .react-datepicker__day--keyboard-selected.react-datepicker__day--keyboard-selected:focus {
    box-shadow: none;
  }

  && .react-datepicker__day:hover {
    background-color: ${getColor('background2')};
    border-radius: 50%;
  }

  && .react-datepicker__day:focus {
    outline: none;
    box-shadow: 0 0 4px 1px ${getColor('global1')};
  }

  && .react-datepicker__navigation.react-datepicker__navigation--previous:focus,
  && .react-datepicker__navigation.react-datepicker__navigation--next:focus {
    outline: none;
  }
`;

const DatePicker = ({className, ...rest}: DatePickerProps) => (
  <Container className={className}>
    <ReactDatePicker
      // @ts-expect-error: wrong props
      customInput={<CustomInput />}
      locale="en-GB"
      {...rest}
    />
  </Container>
);

export default DatePicker;
